import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Users, DollarSign, Clock, CheckCircle, Check, X, AlertCircle } from 'lucide-react';
import { usePage } from '@inertiajs/react';
import { AffiliateLayout } from '../../../design-system/components/layouts';
import {
  StatCard,
  Card,
  CardHeader,
  CardBody,
  DataTable,
  Badge,
  Button,
  Input,
  CopyToClipboard,
  InlineEdit,
  PageHeader,
  Tooltip,
} from '../../../design-system/components';
import { formatCurrency, formatDate } from '../../../lib/formatters';
import {
  affiliate_update_slug_path,
  affiliate_update_coupon_path,
  affiliate_update_paypal_path,
  affiliate_check_slug_availability_path,
  affiliate_check_coupon_availability_path,
  affiliate_dashboard_path,
} from '../../../lib/routes';
import { router } from '@inertiajs/react';
function SlugEditor({ currentSlug, productUrl, readOnly }) {
  if (readOnly) {
    return <span className="text-sm text-fg">{currentSlug}</span>;
  }
  const [editing, setEditing] = useState(false);
  const [slug, setSlug] = useState(currentSlug);
  const [status, setStatus] = useState('idle');
  const [statusMessage, setStatusMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const debounceRef = useRef(undefined);
  const inputRef = useRef(null);
  const displayUrl = productUrl.replace(/^https?:\/\//, '');
  useEffect(() => {
    setSlug(currentSlug);
  }, [currentSlug]);
  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);
  const checkAvailability = useCallback(
    (value) => {
      if (value === currentSlug) {
        setStatus('available');
        setStatusMessage('Current slug');
        return;
      }
      if (value.length < 2) {
        setStatus('invalid');
        setStatusMessage('Must be at least 2 characters');
        return;
      }
      if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(value)) {
        setStatus('invalid');
        setStatusMessage('Only lowercase letters, numbers, and hyphens');
        return;
      }
      setStatus('checking');
      setStatusMessage('Checking...');
      fetch(`${affiliate_check_slug_availability_path()}?slug=${encodeURIComponent(value)}`, {
        headers: {
          Accept: 'application/json',
        },
      })
        .then((res) => res.json())
        .then((data) => {
          setStatus(data.available ? 'available' : 'unavailable');
          setStatusMessage(data.message);
        })
        .catch(() => {
          setStatus('idle');
          setStatusMessage('');
        });
    },
    [currentSlug],
  );
  const handleChange = (e) => {
    const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setSlug(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!value) {
      setStatus('idle');
      setStatusMessage('');
      return;
    }
    debounceRef.current = setTimeout(() => checkAvailability(value), 300);
  };
  const handleSubmit = () => {
    if (slug === currentSlug) {
      setEditing(false);
      return;
    }
    if (status !== 'available') return;
    setSubmitting(true);
    router.patch(
      affiliate_update_slug_path(),
      {
        slug,
      },
      {
        preserveScroll: true,
        onSuccess: () => {
          setEditing(false);
          setSubmitting(false);
        },
        onError: () => setSubmitting(false),
      },
    );
  };
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    } else if (e.key === 'Escape') {
      setSlug(currentSlug);
      setEditing(false);
      setStatus('idle');
      setStatusMessage('');
    }
  };
  const handleCancel = () => {
    setSlug(currentSlug);
    setEditing(false);
    setStatus('idle');
    setStatusMessage('');
  };
  if (editing) {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Input
            ref={inputRef}
            value={slug}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            disabled={submitting}
            maxLength={50}
            suffix={
              <>
                {status === 'checking' && (
                  <span className="text-xs text-fg-tertiary">Checking...</span>
                )}
                {status === 'available' && (
                  <Check size={16} className="text-green-600" />
                )}
                {status === 'unavailable' && (
                  <X size={16} className="text-red-600" />
                )}
              </>
            }
          />
          <Button
            variant="secondary"
            size="sm"
            onClick={handleSubmit}
            disabled={submitting || status !== 'available' || slug === currentSlug}
            loading={submitting}
          >
            Save
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleCancel}
          >
            Cancel
          </Button>
        </div>
        {statusMessage && (status === 'invalid' || status === 'unavailable') && (
          <p className="text-xs text-red-600">{statusMessage}</p>
        )}
        <p className="text-xs text-fg-tertiary">
          Preview:{' '}
          <span className="font-mono">
            {displayUrl}?via={slug || 'yourslug'}
          </span>
        </p>
      </div>
    );
  }
  return (
    <div className="group flex items-center gap-2">
      <span className="text-sm text-fg">{currentSlug}</span>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setEditing(true)}
        className="!px-1.5 !py-0.5 text-xs text-fg-tertiary opacity-0 group-hover:opacity-100 transition-opacity"
      >
        Edit
      </Button>
    </div>
  );
}
function CouponEditor({ currentCode, readOnly }) {
  if (readOnly) {
    if (!currentCode) {
      return <span className="text-sm text-fg-tertiary italic">No coupon code set</span>;
    }
    return <span className="text-sm font-mono text-fg">{currentCode}</span>;
  }
  const [editing, setEditing] = useState(false);
  const [code, setCode] = useState(currentCode || '');
  const [status, setStatus] = useState('idle');
  const [statusMessage, setStatusMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const debounceRef = useRef(undefined);
  const inputRef = useRef(null);
  useEffect(() => {
    setCode(currentCode || '');
  }, [currentCode]);
  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);
  const checkAvailability = useCallback(
    (value) => {
      if (value === currentCode) {
        setStatus('available');
        setStatusMessage('Current code');
        return;
      }
      if (value.length < 2) {
        setStatus('invalid');
        setStatusMessage('Must be at least 2 characters');
        return;
      }
      setStatus('checking');
      setStatusMessage('Checking...');
      fetch(
        `${affiliate_check_coupon_availability_path()}?coupon_code=${encodeURIComponent(value)}`,
        {
          headers: {
            Accept: 'application/json',
          },
        },
      )
        .then((res) => res.json())
        .then((data) => {
          setStatus(data.available ? 'available' : 'unavailable');
          setStatusMessage(data.message);
        })
        .catch(() => {
          setStatus('idle');
          setStatusMessage('');
        });
    },
    [currentCode],
  );
  const handleChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9-_]/g, '');
    setCode(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!value) {
      setStatus('idle');
      setStatusMessage('');
      return;
    }
    debounceRef.current = setTimeout(() => checkAvailability(value), 300);
  };
  const handleSubmit = () => {
    if (code === currentCode) {
      setEditing(false);
      return;
    }
    if (status !== 'available') return;
    setSubmitting(true);
    router.patch(
      affiliate_update_coupon_path(),
      {
        coupon_code: code,
      },
      {
        preserveScroll: true,
        onSuccess: () => {
          setEditing(false);
          setSubmitting(false);
        },
        onError: () => setSubmitting(false),
      },
    );
  };
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    } else if (e.key === 'Escape') {
      setCode(currentCode || '');
      setEditing(false);
      setStatus('idle');
      setStatusMessage('');
    }
  };
  const handleCancel = () => {
    setCode(currentCode || '');
    setEditing(false);
    setStatus('idle');
    setStatusMessage('');
  };
  if (editing) {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Input
            ref={inputRef}
            value={code}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            disabled={submitting}
            className="font-mono uppercase"
            maxLength={50}
            suffix={
              <>
                {status === 'checking' && (
                  <span className="text-xs text-fg-tertiary">Checking...</span>
                )}
                {status === 'available' && (
                  <Check size={16} className="text-green-600" />
                )}
                {status === 'unavailable' && (
                  <X size={16} className="text-red-600" />
                )}
              </>
            }
          />
          <Button
            variant="secondary"
            size="sm"
            onClick={handleSubmit}
            disabled={submitting || status !== 'available' || code === currentCode}
            loading={submitting}
          >
            Save
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleCancel}
          >
            Cancel
          </Button>
        </div>
        {statusMessage && (status === 'invalid' || status === 'unavailable') && (
          <p className="text-xs text-red-600">{statusMessage}</p>
        )}
      </div>
    );
  }
  if (!currentCode) {
    return <span className="text-sm text-fg-tertiary italic">No coupon code set</span>;
  }
  return (
    <div className="group flex items-center gap-2">
      <span className="text-sm font-mono text-fg">{currentCode}</span>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setEditing(true)}
        className="!px-1.5 !py-0.5 text-xs text-fg-tertiary opacity-0 group-hover:opacity-100 transition-opacity"
      >
        Edit
      </Button>
    </div>
  );
}
const statusVariant = {
  pending: 'warning',
  approved: 'info',
  paid: 'success',
  denied: 'danger',
  voided: 'default',
};
const commissionColumns = [
  {
    id: 'created_at',
    header: 'Date',
    accessor: 'created_at',
    sortable: true,
    render: (commission) => formatDate(commission.created_at),
  },
  {
    id: 'invoice_amount_cents',
    header: 'Invoice',
    accessor: 'invoice_amount_cents',
    render: (commission) => formatCurrency(commission.invoice_amount_cents),
  },
  {
    id: 'commission_amount_cents',
    header: 'Commission',
    accessor: 'commission_amount_cents',
    sortable: true,
    render: (commission) => (
      <span className="font-medium">{formatCurrency(commission.commission_amount_cents)}</span>
    ),
  },
  {
    id: 'commission_rate_snapshot',
    header: 'Rate',
    accessor: 'commission_rate_snapshot',
    render: (commission) => `${(commission.commission_rate_snapshot * 100).toFixed(0)}%`,
  },
  {
    id: 'status',
    header: 'Status',
    accessor: 'status',
    sortable: true,
    render: (commission) => (
      <Badge variant={statusVariant[commission.status] || 'default'} size="sm">
        {commission.status}
      </Badge>
    ),
  },
];
export default function Show({
  affiliate,
  campaign,
  stats,
  commissions,
  product_url,
  pagination,
  sort,
}) {
  const { props } = usePage();
  const readOnly = !!props.impersonating;
  const displayUrl = product_url.replace(/^https?:\/\//, '');
  const referralLink = `${product_url}?via=${affiliate.slug}`;
  const handleSort = (column, direction) => {
    router.visit(affiliate_dashboard_path(), {
      data: {
        sort: column,
        direction,
        page: 1,
      },
      preserveState: true,
      preserveScroll: true,
      replace: true,
    });
  };
  const handlePageChange = (page) => {
    router.visit(affiliate_dashboard_path(), {
      data: {
        sort: sort.column,
        direction: sort.direction,
        page,
      },
      preserveState: true,
      preserveScroll: true,
      replace: true,
    });
  };
  return (
    <AffiliateLayout>
      <PageHeader
        title={`Welcome back, ${affiliate.name.split(' ')[0]}`}
        description="Track your referrals, earnings, and manage your affiliate settings."
      />

      {/* Stats Grid */}
      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Total Referrals"
          value={stats.signups_count}
          icon={<Users size={20} />}
        />
        <StatCard
          label="Total Earned"
          value={formatCurrency(stats.total_earned_cents)}
          icon={<DollarSign size={20} />}
        />
        <StatCard
          label="Pending Earnings"
          value={formatCurrency(stats.pending_balance_cents)}
          icon={<Clock size={20} />}
        />
        <StatCard
          label="Paid Earnings"
          value={formatCurrency(stats.paid_cents)}
          icon={<CheckCircle size={20} />}
        />
      </div>

      {/* Referral Link & Coupon Section */}
      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Referral Link */}
        <Card>
          <CardHeader>
            <h3 className="text-sm font-medium text-fg">Your Referral Link</h3>
          </CardHeader>
          <CardBody>
            <div className="space-y-3">
              <div className="rounded-lg bg-surface-secondary border border-edge-strong p-3">
                <CopyToClipboard text={referralLink} className="w-full">
                  <span className="text-sm font-mono text-fg-secondary break-all flex-1">
                    {referralLink}
                  </span>
                </CopyToClipboard>
              </div>
              <div className="flex items-center justify-between text-xs text-fg-tertiary">
                <span>{stats.clicks_count} clicks</span>
                <span>{stats.link_referrals_count} link referrals</span>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Coupon Code */}
        {campaign?.coupon_enabled && (
          <Card>
            <CardHeader>
              <h3 className="text-sm font-medium text-fg">Your Coupon Code</h3>
            </CardHeader>
            <CardBody>
              <div className="space-y-3">
                {affiliate.coupon_code && (
                  <div className="rounded-lg bg-surface-secondary border border-edge-strong p-3">
                    <CopyToClipboard text={affiliate.coupon_code} className="w-full">
                      <span className="text-sm font-mono font-semibold text-fg flex-1">
                        {affiliate.coupon_code}
                      </span>
                    </CopyToClipboard>
                  </div>
                )}
                {!affiliate.coupon_code && (
                  <p className="text-sm text-fg-tertiary italic">No coupon code set</p>
                )}
                <div className="flex items-center justify-between text-xs text-fg-tertiary">
                  <span>{stats.coupon_referrals_count} coupon referrals</span>
                  {campaign.default_coupon_discount_type &&
                    campaign.default_coupon_discount_value && (
                      <span>
                        {campaign.default_coupon_discount_type === 'percent'
                          ? `${campaign.default_coupon_discount_value}% off`
                          : `$${(campaign.default_coupon_discount_value / 100).toFixed(0)} off`}
                      </span>
                    )}
                </div>
              </div>
            </CardBody>
          </Card>
        )}
      </div>

      {/* Referral Stats Breakdown */}
      <div className="mt-8">
        <Card>
          <CardHeader>
            <h3 className="text-sm font-medium text-fg">Referral Breakdown</h3>
          </CardHeader>
          <CardBody>
            <div className="grid grid-cols-2 gap-6 sm:grid-cols-4">
              <div>
                <p className="text-xs font-medium text-fg-tertiary uppercase tracking-wider">
                  Clicks
                </p>
                <p className="mt-1 text-xl font-semibold text-fg">{stats.clicks_count}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-fg-tertiary uppercase tracking-wider">
                  Sign-ups
                </p>
                <p className="mt-1 text-xl font-semibold text-fg">{stats.signups_count}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-fg-tertiary uppercase tracking-wider">
                  Conversions
                </p>
                <p className="mt-1 text-xl font-semibold text-fg">
                  {stats.conversions_count}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-fg-tertiary uppercase tracking-wider">
                  Conversion Rate
                </p>
                <p className="mt-1 text-xl font-semibold text-fg">
                  {stats.signups_count > 0
                    ? `${((stats.conversions_count / stats.signups_count) * 100).toFixed(1)}%`
                    : '0%'}
                </p>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Commission History */}
      <div className="mt-8">
        <Card>
          <CardHeader>
            <h3 className="text-sm font-medium text-fg">Commission History</h3>
          </CardHeader>
          <DataTable
            columns={commissionColumns}
            data={commissions}
            keyAccessor="id"
            sortColumn={sort.column}
            sortDirection={sort.direction}
            onSort={handleSort}
            pagination={
              pagination.total_pages > 1
                ? {
                    page: pagination.current_page,
                    totalPages: pagination.total_pages,
                    onPageChange: handlePageChange,
                  }
                : undefined
            }
            emptyTitle="No commissions yet"
            emptyDescription="Start sharing your referral link to earn commissions."
            className="border-0 rounded-t-none"
          />
        </Card>
      </div>

      {/* Settings Section */}
      <div className="mt-8">
        <Card>
          <CardHeader>
            <h3 className="text-sm font-medium text-fg">Account Settings</h3>
          </CardHeader>
          <CardBody>
            <dl className="divide-y divide-neutral-100">
              <div className="flex items-start justify-between py-3 first:pt-0 last:pb-0">
                <dt className="text-sm font-medium text-fg-tertiary w-32 flex-shrink-0">
                  Referral Slug
                </dt>
                <dd className="flex-1 text-right">
                  <SlugEditor currentSlug={affiliate.slug} productUrl={product_url} readOnly={readOnly} />
                </dd>
              </div>

              {campaign?.coupon_enabled && (
                <div className="flex items-start justify-between py-3">
                  <dt className="text-sm font-medium text-fg-tertiary w-32 flex-shrink-0">
                    Coupon Code
                  </dt>
                  <dd className="flex-1 text-right">
                    <div className="flex justify-end">
                      <CouponEditor currentCode={affiliate.coupon_code} readOnly={readOnly} />
                    </div>
                  </dd>
                </div>
              )}

              <div className="flex items-start justify-between py-3 last:pb-0">
                <dt className="text-sm font-medium text-fg-tertiary w-32 flex-shrink-0">
                  PayPal Email
                </dt>
                <dd className="flex-1 text-right">
                  <div className="flex items-center justify-end gap-1.5">
                    {readOnly ? (
                      <span className="text-sm text-fg">
                        {affiliate.paypal_email || <span className="text-fg-tertiary italic">Not set</span>}
                      </span>
                    ) : (
                      <InlineEdit
                        value={affiliate.paypal_email || ''}
                        url={affiliate_update_paypal_path()}
                        paramName="paypal_email"
                        placeholder="Set PayPal email"
                        inputType="email"
                      />
                    )}
                    {affiliate.paypal_email && !affiliate.unconfirmed_paypal_email && affiliate.paypal_email_confirmed_at && (
                      <Tooltip content="Confirmed">
                        <CheckCircle size={14} className="text-emerald-500" />
                      </Tooltip>
                    )}
                  </div>
                  {affiliate.unconfirmed_paypal_email && (
                    <div className="flex items-center justify-end gap-1.5 mt-1.5">
                      <Badge variant="warning" size="sm">
                        <AlertCircle size={12} className="mr-1" />
                        Pending
                      </Badge>
                      <span className="text-xs text-fg-tertiary">
                        Check inbox at {affiliate.unconfirmed_paypal_email}
                      </span>
                    </div>
                  )}
                </dd>
              </div>
            </dl>
          </CardBody>
        </Card>
      </div>
    </AffiliateLayout>
  );
}
